/**
 * Per-person shift choices on sign-up's "Add your team" — pure helpers, no I/O.
 *
 * The register body carries no shift (every member starts on the company default, General
 * 10:00–19:00, from apps/api's ensureDefaultShift). The owner's other picks are applied AFTER the
 * workspace exists, from Home, through the ordinary shift routes (SignupShiftApplier). Between the
 * two, the picks ride in the landing URL (`/?invite=sms&shifts=…`) because the sign-up page and the
 * new company's subdomain are different origins on the web — a value in localStorage here would not
 * be readable there. The URL carries a short hash of each number, never the number itself, so no
 * phone number ends up in a server log or in the address bar.
 */

/** The four ready-made shifts the sheet offers. General is the company default. */
export const SHIFT_PRESETS = {
  general: {
    name: 'General',
    startTime: '10:00',
    endTime: '19:00',
    detail: '9 hrs · 1 hr lunch break',
  },
  morning: {
    name: 'Morning',
    startTime: '07:00',
    endTime: '15:30',
    detail: '8.5 hrs · Early production shift',
  },
  evening: {
    name: 'Evening',
    startTime: '14:00',
    endTime: '22:30',
    detail: '8.5 hrs · Afternoon / second shift',
  },
  night: {
    name: 'Night',
    startTime: '22:00',
    endTime: '06:30',
    detail: '8.5 hrs · Overnight / graveyard shift',
  },
} as const;

export type ShiftPresetKey = keyof typeof SHIFT_PRESETS;
export const SHIFT_PRESET_KEYS = Object.keys(SHIFT_PRESETS) as ShiftPresetKey[];

export type ShiftChoice =
  | { kind: ShiftPresetKey }
  | { kind: 'custom'; startTime: string; endTime: string };

/** A member's choice, by their E.164 number. Missing means General. */
export type TeamShifts = Record<string, ShiftChoice>;

export const GENERAL_SHIFT: ShiftChoice = { kind: 'general' };

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isTime(value: unknown): value is string {
  return typeof value === 'string' && HHMM.test(value);
}

function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h! * 60 + m!;
}

/** The shift's window in minutes; one that ends at or before it starts runs past midnight. */
export function windowMinutes(startTime: string, endTime: string): number {
  const span = minutesOf(endTime) - minutesOf(startTime);
  return span <= 0 ? span + 24 * 60 : span;
}

/** Why a custom start/end cannot be saved, or null when it can. */
export function customTimingProblem(startTime: string, endTime: string): string | null {
  if (!isTime(startTime) || !isTime(endTime)) return 'Enter a start and an end time.';
  if (startTime === endTime) return 'The end time must differ from the start time.';
  const span = windowMinutes(startTime, endTime);
  if (span < 2 * 60) return 'A shift needs to be at least 2 hours long.';
  if (span > 16 * 60) return 'A shift can be at most 16 hours long.';
  return null;
}

export function isShiftChoice(value: unknown): value is ShiftChoice {
  if (!value || typeof value !== 'object') return false;
  const choice = value as { kind?: unknown; startTime?: unknown; endTime?: unknown };
  if (choice.kind === 'custom') {
    return (
      isTime(choice.startTime) &&
      isTime(choice.endTime) &&
      customTimingProblem(choice.startTime, choice.endTime) === null
    );
  }
  return typeof choice.kind === 'string' && Object.hasOwn(SHIFT_PRESETS, choice.kind);
}

/** A well-formed TeamShifts from anything (a stored draft); malformed entries are dropped. */
export function sanitizeTeamShifts(value: unknown): TeamShifts {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: TeamShifts = {};
  for (const [phone, choice] of Object.entries(value as Record<string, unknown>)) {
    if (isShiftChoice(choice)) out[phone] = choice;
  }
  return out;
}

export function shiftTimes(choice: ShiftChoice): { startTime: string; endTime: string } {
  return choice.kind === 'custom'
    ? { startTime: choice.startTime, endTime: choice.endTime }
    : { startTime: SHIFT_PRESETS[choice.kind].startTime, endTime: SHIFT_PRESETS[choice.kind].endTime };
}

export function shiftName(choice: ShiftChoice): string {
  return choice.kind === 'custom' ? 'Custom' : SHIFT_PRESETS[choice.kind].name;
}

/** "General · 10:00 – 19:00", the member row's label. */
export function shiftLabel(choice: ShiftChoice): string {
  const { startTime, endTime } = shiftTimes(choice);
  return `${shiftName(choice)} · ${startTime} – ${endTime}`;
}

export function isGeneral(choice: ShiftChoice | undefined): boolean {
  if (!choice) return true;
  const { startTime, endTime } = shiftTimes(choice);
  return (
    startTime === SHIFT_PRESETS.general.startTime && endTime === SHIFT_PRESETS.general.endTime
  );
}

/** The choices that need a change after sign-up: members still in the team, not on General. */
export function nonDefaultShifts(
  team: readonly { phone: string }[],
  shifts: TeamShifts,
): { phone: string; choice: ShiftChoice }[] {
  return team
    .map((member) => ({ phone: member.phone, choice: shifts[member.phone] }))
    .filter((entry): entry is { phone: string; choice: ShiftChoice } => !isGeneral(entry.choice));
}

// ---- The landing-URL plan -------------------------------------------------------------------

/** The landing URL's param that carries the plan. */
export const SHIFT_PLAN_PARAM = 'shifts';

/**
 * A 64-bit FNV-1a hash of the number's digits, as 16 hex characters. Not a secret (the space of
 * phone numbers is small): it keeps the number itself out of URLs and logs, and is plenty to tell
 * apart the handful of people a new company has. Digits only, so "+91 98765 43210" and
 * "+919876543210" agree.
 */
export function phoneKey(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < digits.length; i++) {
    hash ^= BigInt(digits.charCodeAt(i));
    hash = (hash * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, '0');
}

export interface ShiftPlanEntry {
  key: string;
  choice: ShiftChoice;
}

const PRESET_CODE: Record<Exclude<ShiftPresetKey, 'general'>, string> = {
  morning: 'm',
  evening: 'e',
  night: 'n',
};

function encodeChoice(choice: ShiftChoice): string | null {
  if (choice.kind === 'custom') {
    return `c${choice.startTime.replace(':', '')}${choice.endTime.replace(':', '')}`;
  }
  return choice.kind === 'general' ? null : PRESET_CODE[choice.kind];
}

function decodeChoice(code: string): ShiftChoice | null {
  if (code === 'm') return { kind: 'morning' };
  if (code === 'e') return { kind: 'evening' };
  if (code === 'n') return { kind: 'night' };
  const custom = /^c(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(code);
  if (!custom) return null;
  const choice: ShiftChoice = {
    kind: 'custom',
    startTime: `${custom[1]}:${custom[2]}`,
    endTime: `${custom[3]}:${custom[4]}`,
  };
  return isShiftChoice(choice) ? choice : null;
}

/** `<key>.<code>~<key>.<code>`, or '' when everyone stays on General. */
export function encodeShiftPlan(
  team: readonly { phone: string }[],
  shifts: TeamShifts,
): string {
  return nonDefaultShifts(team, shifts)
    .map(({ phone, choice }) => {
      const code = encodeChoice(choice);
      return code ? `${phoneKey(phone)}.${code}` : null;
    })
    .filter((part): part is string => part !== null)
    .join('~');
}

/** The plan off the URL; anything malformed is skipped, never thrown. */
export function decodeShiftPlan(raw: string | null | undefined): ShiftPlanEntry[] {
  if (!raw) return [];
  const entries: ShiftPlanEntry[] = [];
  for (const part of raw.split('~').slice(0, 50)) {
    const match = /^([0-9a-f]{16})\.(.+)$/.exec(part);
    if (!match) continue;
    const choice = decodeChoice(match[2]!);
    if (choice) entries.push({ key: match[1]!, choice });
  }
  return entries;
}

/** Where sign-up lands: Home's SMS invites when people were added, with any shift plan. */
export function signupLandingPath(
  team: readonly { phone: string }[],
  shifts: TeamShifts = {},
): string {
  if (team.length === 0) return '/';
  const plan = encodeShiftPlan(team, shifts);
  return plan ? `/?invite=sms&${SHIFT_PLAN_PARAM}=${plan}` : '/?invite=sms';
}

// ---- Planning the API calls ------------------------------------------------------------------

export interface ExistingShift {
  id: string;
  code: string;
  startTime: string;
  endTime: string;
  isActive?: boolean;
}

/** The createShift body for a shift that does not exist yet (mirrors ensureDefaultShift). */
export interface NewShiftSpec {
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  graceMinutes: number;
  halfDayThresholdHours: number;
  fullDayThresholdHours: number;
  isNightShift: boolean;
  weeklyOffDays: number[];
}

export interface ShiftGroup {
  /** `HH:MM-HH:MM`, the group's identity. */
  timing: string;
  /** The org's existing shift with these times, when there is one. */
  shiftId: string | null;
  /** What to create when there is none. */
  create: NewShiftSpec | null;
  personIds: string[];
}

export interface ShiftApplyPlan {
  groups: ShiftGroup[];
  /** Plan entries no employee matched (a number that did not become an employee). */
  unmatched: number;
}

/** A created shift's code: 2–16 characters, unique per org (createShiftPayloadSchema). */
function shiftCodeFor(choice: ShiftChoice, startTime: string, endTime: string): string {
  const compact = `${startTime.replace(':', '')}_${endTime.replace(':', '')}`;
  if (choice.kind === 'custom') return `CUST_${compact}`;
  const prefix = { general: 'GEN', morning: 'MORN', evening: 'EVE', night: 'NIGHT' }[choice.kind];
  return `${prefix}_${compact}`;
}

export function newShiftSpec(choice: ShiftChoice): NewShiftSpec {
  const { startTime, endTime } = shiftTimes(choice);
  const span = windowMinutes(startTime, endTime);
  // General's 9-hour window has an hour's lunch; the 8.5-hour shifts half an hour, so the paid day
  // is 8 hours — the full-day threshold — rather than falling short of it every day.
  const breakMinutes = span >= 9 * 60 ? 60 : span >= 6 * 60 ? 30 : 0;
  const paidHours = (span - breakMinutes) / 60;
  const fullDayThresholdHours = Math.max(2, Math.min(8, Math.floor(paidHours * 2) / 2));
  const halfDayThresholdHours = Math.max(1, Math.min(4, fullDayThresholdHours / 2));
  return {
    code: shiftCodeFor(choice, startTime, endTime),
    name:
      choice.kind === 'custom'
        ? `Custom ${startTime}–${endTime}`
        : SHIFT_PRESETS[choice.kind].name,
    startTime,
    endTime,
    breakMinutes,
    graceMinutes: 15,
    halfDayThresholdHours,
    fullDayThresholdHours,
    // Runs past midnight: the same rule the server's shiftWindowMinutes applies.
    isNightShift: endTime <= startTime,
    // Sunday off, as the default shift.
    weeklyOffDays: [0],
  };
}

/**
 * Turns the plan into shift groups: each distinct timing once, reusing an active shift the org
 * already has with the same start and end (no duplicates), and the person ids found by number.
 */
export function planShiftApplication(
  entries: readonly ShiftPlanEntry[],
  employees: readonly { personId: string; phone: string | null }[],
  existingShifts: readonly ExistingShift[],
): ShiftApplyPlan {
  const personByKey = new Map<string, string>();
  for (const employee of employees) {
    if (employee.phone) personByKey.set(phoneKey(employee.phone), employee.personId);
  }
  const groups = new Map<string, ShiftGroup>();
  let unmatched = 0;
  const seenPeople = new Set<string>();
  for (const entry of entries) {
    const personId = personByKey.get(entry.key);
    if (!personId) {
      unmatched += 1;
      continue;
    }
    if (seenPeople.has(personId)) continue;
    seenPeople.add(personId);
    const { startTime, endTime } = shiftTimes(entry.choice);
    const timing = `${startTime}-${endTime}`;
    let group = groups.get(timing);
    if (!group) {
      const existing = existingShifts.find(
        (shift) =>
          shift.isActive !== false && shift.startTime === startTime && shift.endTime === endTime,
      );
      group = {
        timing,
        shiftId: existing?.id ?? null,
        create: existing ? null : newShiftSpec(entry.choice),
        personIds: [],
      };
      groups.set(timing, group);
    }
    group.personIds.push(personId);
  }
  return { groups: [...groups.values()], unmatched };
}

/** A person's standing assignment: the open-ended one, the latest when there are several. */
export function standingAssignment<T extends { id: string; validFrom: string; validTo: string | null }>(
  rows: readonly T[],
): T | null {
  return (
    [...rows]
      .filter((row) => row.validTo === null)
      .sort((a, b) => (a.validFrom < b.validFrom ? 1 : -1))[0] ?? null
  );
}
